import React, { useState, useEffect } from 'react';
import { 
  Crown, 
  Gauge, 
  Users, 
  Building2, 
  Box, 
  Check, 
  ArrowRight
} from 'lucide-react';

interface PricingOverviewProps {
  onOpenUpgradeModal: () => void;
  currentTier?: string;
  preferredCurrency?: string;
}

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar', rate: 1 },
  { code: 'UGX', symbol: 'UGX ', name: 'Ugandan Shilling', rate: 3800 },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', rate: 1450 },
  { code: 'KES', symbol: 'KSh ', name: 'Kenyan Shilling', rate: 130 }
];

export const PricingOverview: React.FC<PricingOverviewProps> = ({
  onOpenUpgradeModal,
  currentTier = 'free',
  preferredCurrency = 'USD'
}) => {
  const [activeTab, setActiveTab] = useState<'individual' | 'corporate'>('individual');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [selectedCurrency, setSelectedCurrency] = useState<'USD' | 'UGX' | 'NGN' | 'KES'>('USD');

  useEffect(() => {
    if (preferredCurrency && ['USD', 'UGX', 'NGN', 'KES'].includes(preferredCurrency)) {
      setSelectedCurrency(preferredCurrency as any);
    }
  }, [preferredCurrency]);

  const getDisplayPrice = (usdMonthly: number, usdAnnual: number, hasAnnual: boolean = true) => {
    const isAnnual = billingCycle === 'annual' && hasAnnual;
    const usdAmount = isAnnual ? usdAnnual : usdMonthly;
    
    if (selectedCurrency === 'USD') {
      return { 
        priceStr: `$${usdAmount}/${isAnnual ? 'yr' : 'mo'}`, 
        amount: usdAmount, 
        note: isAnnual ? 'Paid annually' : 'Paid monthly' 
      };
    } else if (selectedCurrency === 'UGX') {
      const ugxAmount = isAnnual 
        ? (usdAnnual === 190 ? 725000 : usdAnnual === 490 ? 1800000 : 3650000) 
        : (usdMonthly === 19 ? 72500 : usdMonthly === 49 ? 180000 : 365000);
      return { 
        priceStr: `${ugxAmount.toLocaleString()} UGX/${isAnnual ? 'yr' : 'mo'}`, 
        amount: ugxAmount, 
        note: isAnnual ? 'Paid annually' : 'Paid monthly' 
      };
    } else if (selectedCurrency === 'NGN') {
      const ngnAmount = isAnnual 
        ? (usdAnnual === 190 ? 280000 : usdAnnual === 490 ? 720000 : 1450000) 
        : (usdMonthly === 19 ? 28000 : usdMonthly === 49 ? 72000 : 145000);
      return { 
        priceStr: `₦${ngnAmount.toLocaleString()}/${isAnnual ? 'yr' : 'mo'}`, 
        amount: ngnAmount, 
        note: isAnnual ? 'Paid annually' : 'Paid monthly' 
      };
    } else { // KES
      const kesAmount = isAnnual 
        ? (usdAnnual === 190 ? 25000 : usdAnnual === 490 ? 65000 : 130000) 
        : (usdMonthly === 19 ? 2500 : usdMonthly === 49 ? 6500 : 13000);
      return { 
        priceStr: `KSh ${kesAmount.toLocaleString()}/${isAnnual ? 'yr' : 'mo'}`, 
        amount: kesAmount, 
        note: isAnnual ? 'Paid annually' : 'Paid monthly' 
      };
    }
  };

  const individualPlans = [
    {
      id: 'free',
      name: 'Free',
      price: '$0/mo',
      monthlyUsd: 0,
      annualUsd: 0,
      hasAnnual: false,
      hasCustom: false,
      icon: Box,
      description: 'Frictionless entry & viral discovery. Build initial business ideas, test local markets, & understand options.',
      features: [
        '2 feasibility studies in Launchpad',
        '1 Scrutiny Deck gap assessment',
        '1 Basic Discovery scan (limited)',
        '5 Dynamic Map Views & 2 Geocodes / mo',
        'NO PDF Report Exports (watermarked)',
        'Read-only War Room access (no saves)',
        'Full Hub visibility with feature previews'
      ]
    },
    {
      id: 'premium',
      name: 'Premium',
      monthlyUsd: 19,
      annualUsd: 190,
      hasAnnual: true,
      hasCustom: false,
      icon: Crown,
      tag: 'Most Popular',
      description: 'The definitive suite for serious founders seeking structured intelligence and full feasibility output.',
      features: [
        'Unlimited intelligence searches in Launchpad',
        'Unlimited scans in Discovery Hub',
        '80 Dynamic Map Views & 20 Geocodes / mo',
        'Full PDF report exports (no watermarks)',
        'Priority server queuing for Gemini jobs',
        'Unlimited War Room saves and comparisons',
        'Email support (24-hour turnaround)'
      ]
    },
    {
      id: 'optimize',
      name: 'Optimize',
      monthlyUsd: 49,
      annualUsd: 490,
      hasAnnual: true,
      hasCustom: false,
      icon: Gauge,
      description: 'The master suite for operational scale, performance benchmarking, and active portfolio management.',
      features: [
        'Everything in Premium Tier',
        '250 Dynamic Maps & 50 Geocodes / mo',
        '50 Distance Matrix Proximity Elements',
        'Full Operational Health Check',
        'GrowthMath Modeling Engine',
        'Interactive Strategy Sandbox',
        'Live Ops Dashboard',
        'Living SWOT with status sync',
        'Expansion Scout & Exit Architect',
        '1 connected live business data source',
        '5 team member seats included'
      ]
    }
  ];

  const corporatePlans = [
    {
      id: 'team',
      name: 'Team',
      monthlyUsd: 99,
      annualUsd: 990,
      hasAnnual: true,
      hasCustom: false,
      icon: Users,
      tag: 'Mid-Market Standard',
      description: 'Unified coordination, team-wide insights, secure stakeholders, and ERP/CRM integration pipelines.',
      features: [
        'Everything in Optimize Tier',
        '3,000 Pooled Dynamic Maps / mo',
        '300 Geocodes & 500 Distance Matrix',
        '5 full seat allocations by default',
        'Multi-user collaboration with custom RBAC',
        'Secure stakeholder link sharing',
        'Salesforce & HubSpot CRM integration',
        'ERP synchronicity (API synced variables)',
        'Localized international datasets',
        'Team annotations & action items on SWOT',
        'SLA priority email support (4-hour pledge)'
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      monthlyUsd: 0,
      annualUsd: 0,
      hasCustom: true,
      icon: Building2,
      description: 'Tailored deployment, global scale, customized analytics modules, and direct executive oversight.',
      features: [
        'Everything in Team Tier',
        'Unlimited Dynamic Maps & Geocoding',
        'Unlimited Distance Matrix & Directions',
        'Unlimited team seat allocation',
        'White-label reports with branded PDFs & custom domain',
        'On-premise deployment or dedicated Cloud Run',
        'Dedicated Customer Success Manager (CSM)',
        'Quarterly business reviews & custom sector modeling',
        'Bulk API access with custom limit rules',
        'Guaranteed uptime & latency SLA agreements',
        'Advanced offline deployment options for field work',
        'Full government compliance audit logs'
      ]
    }
  ];

  const plans = activeTab === 'individual' ? individualPlans : corporatePlans;

  return (
    <section className="w-full py-12" id="pricing-overview-section">
      <div className="text-center max-w-2xl mx-auto space-y-3 mb-10">
        <h2 className="text-3xl sm:text-4xl font-medium text-white tracking-tight font-heading">
          Scale Your Market Intelligence
        </h2>
        <p className="text-sm text-white/60 max-w-lg mx-auto">
          Start for free to discover local opportunities, or upgrade for comprehensive financial, operational, and geospatial intelligence.
        </p>
      </div>

      {/* Control Bar */}
      <div className="max-w-6xl mx-auto mb-8 bg-[#3a3430] p-4 border border-white/[0.06] rounded-xl flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Tab Toggle */}
        <div className="flex border border-white/[0.06] p-1 bg-[#322D29] rounded-lg">
          <button
            onClick={() => setActiveTab('individual')}
            className={`px-4 py-2 font-bold uppercase text-[9px] tracking-wider transition-all rounded-md cursor-pointer ${
              activeTab === 'individual' 
                ? 'bg-[#3a3430] text-white border border-white/[0.08] shadow-sm' 
                : 'text-white/40 hover:text-white/80'
            }`}
          >
            Individual Plans
          </button>
          <button
            onClick={() => setActiveTab('corporate')}
            className={`px-4 py-2 font-bold uppercase text-[9px] tracking-wider transition-all rounded-md cursor-pointer ${
              activeTab === 'corporate' 
                ? 'bg-[#3a3430] text-white border border-white/[0.08] shadow-sm' 
                : 'text-white/40 hover:text-white/80'
            }`}
          >
            Corporate / Company Tiers
          </button>
        </div>

        {/* Billing Cycle Toggle */}
        <div className="flex items-center gap-3">
          <span className={`text-[10px] uppercase font-bold tracking-wider ${billingCycle === 'monthly' ? 'text-white' : 'text-white/40'}`}>
            Monthly
          </span>
          <button 
            onClick={() => setBillingCycle(prev => prev === 'monthly' ? 'annual' : 'monthly')}
            className="w-12 h-6 border border-white/10 bg-[#322D29] rounded-full relative flex items-center transition-all px-0.5 cursor-pointer"
            aria-label="Toggle Billing Cycle"
          >
            <div className={`w-5 h-5 bg-[#F26522] rounded-full transition-transform ${billingCycle === 'annual' ? 'translate-x-6' : 'translate-x-0'}`}></div>
          </button>
          <div className="flex items-center gap-1.5">
            <span className={`text-[10px] uppercase font-bold tracking-wider ${billingCycle === 'annual' ? 'text-white' : 'text-white/40'}`}>
              Annual
            </span>
            <span className="bg-[#F26522] text-white text-[8px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">
              2 Months Free
            </span>
          </div>
        </div>

        {/* Currency Toggle */}
        <div className="flex bg-[#322D29] p-1 border border-white/[0.06] rounded-lg">
          {CURRENCIES.map((curr) => (
            <button
              key={`curr-toggle-landing-${curr.code}`}
              onClick={() => setSelectedCurrency(curr.code as any)}
              className={`px-3 py-1.5 font-bold text-[9px] uppercase tracking-wider rounded-md transition-all cursor-pointer ${
                selectedCurrency === curr.code 
                  ? 'bg-[#3a3430] text-[#F26522] shadow-sm border border-white/[0.06]' 
                  : 'text-white/50 hover:text-white'
              }`}
            >
              {curr.code}
            </button>
          ))}
        </div>
      </div>

      {/* Grid of Plans */}
      <div className={`grid gap-6 max-w-6xl mx-auto ${plans.length === 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2 max-w-4xl'}`}>
        {plans.map((p) => {
          const priceInfo = getDisplayPrice(p.monthlyUsd, p.annualUsd, p.hasAnnual);
          const isCurrent = currentTier.toLowerCase() === p.id.toLowerCase();
          const IconComponent = p.icon;

          return (
            <div 
              key={`landing-plan-card-${p.id}`} 
              className={`bg-[#3a3430] border p-6 sm:p-8 flex flex-col justify-between transition-all relative overflow-hidden group rounded-xl ${
                isCurrent 
                  ? 'border-2 border-[#F26522]' 
                  : p.tag 
                  ? 'border-[#F26522]/40 hover:border-[#F26522]' 
                  : 'border-white/[0.06] hover:border-white/[0.14]'
              }`}
            >
              {p.tag && (
                <div className="absolute top-0 right-0 bg-[#F26522] text-white text-[8px] font-bold uppercase tracking-widest px-3 py-1 font-heading rounded-bl-lg">
                  {p.tag}
                </div>
              )}
              {isCurrent && (
                <div className="absolute top-0 left-0 bg-emerald-600 text-white text-[8px] font-bold uppercase tracking-widest px-3 py-0.5 font-heading rounded-br-lg">
                  Active Plan
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 border border-white/[0.08] bg-[#322D29] rounded-lg flex items-center justify-center">
                    <IconComponent className={`w-5 h-5 ${isCurrent || p.tag ? 'text-[#F26522]' : 'text-white/80'}`} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold uppercase text-white tracking-tight font-heading">
                      {p.name}
                    </h3>
                    {p.hasCustom ? (
                      <p className="text-xl font-bold text-[#F26522] font-heading">Custom Pricing</p>
                    ) : (
                      <p className="text-xl font-bold text-[#F26522] font-heading">{priceInfo.priceStr}</p>
                    )}
                  </div>
                </div>

                <p className="text-[10px] font-medium text-white/60 tracking-wide border-l-2 border-[#F26522]/40 pl-3 py-1 italic leading-relaxed">
                  {p.description}
                </p>

                <div className="h-px w-full my-3 bg-white/[0.06]" />

                <ul className="space-y-2 py-1">
                  {p.features.map((feature, idx) => (
                    <li key={`landing-feat-${p.id}-${idx}`} className="flex items-start gap-2 text-[10px] font-medium uppercase tracking-wider text-white/80 leading-tight">
                      <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-8 pt-4 border-t border-white/[0.06]">
                {isCurrent ? (
                  <button 
                    disabled
                    className="w-full py-4 bg-[#322D29] text-white/40 font-bold uppercase text-[9px] tracking-[0.2em] border border-white/[0.06] cursor-default text-center rounded-lg"
                  >
                    Current Provision
                  </button>
                ) : p.hasCustom ? (
                  <button
                    onClick={onOpenUpgradeModal}
                    className="w-full py-4 bg-[#322D29] hover:bg-[#F26522] text-white font-bold uppercase text-[9px] tracking-[0.2em] border border-white/[0.08] hover:border-[#F26522] transition-all active:scale-[0.98] text-center cursor-pointer flex items-center justify-center gap-2 rounded-lg"
                  >
                    <span>Initiate Consultation</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <button
                    onClick={onOpenUpgradeModal}
                    className="w-full py-4 bg-[#F26522] hover:bg-[#F26522]/90 text-white font-bold uppercase text-[9px] tracking-[0.2em] transition-all active:scale-[0.98] text-center cursor-pointer flex items-center justify-center gap-2 rounded-lg"
                  >
                    <span>Acquire {p.name} Capability</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PricingOverview;
